import { confirmDiscardChangesSubText } from 'owa-locstrings/lib/strings/confirmdiscardchangessubtext.locstring.json';
import { confirmDiscardChangesTitleText } from 'owa-locstrings/lib/strings/confirmdiscardchangestitletext.locstring.json';
import {
    confirmDiscardEventTitleText,
    confirmDiscardEventSubText,
    confirmAttachmentsChangedTitleText,
    confirmAttachmentsChangedSubText,
} from './presentClosureConfirmation.locstring.json';
import { blockOnNavigationCancelLabel } from 'owa-locstrings/lib/strings/blockonnavigationcancellabel.locstring.json';
import { blockOnNavigationContinueLabel } from 'owa-locstrings/lib/strings/blockonnavigationcontinuelabel.locstring.json';
import { blockOnNavigationMessage } from 'owa-locstrings/lib/strings/blockonnavigationmessage.locstring.json';
import { blockOnNavigationTitle } from 'owa-locstrings/lib/strings/blockonnavigationtitle.locstring.json';
import loc from 'owa-localize';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import getChangedAttachmentIds from '../utils/getChangedAttachmentIds';
import getFullComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getFullComposeViewState';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import isDirty from '../utils/isDirty';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import {
    discardEvent,
    prepareEvent,
    presentDiscardClosureConfirmation,
} from 'owa-calendar-compose-form-actions';
import { getComposeHostItemIndex, isAnyNonAutoRunUilessAddinRunning } from 'owa-addins-core';

import { getSchedulingAssistantIsDirty } from 'owa-calendar-scheduling-assistant';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import { orchestrator } from 'satcheljs';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';

export const presentDiscardClosureConfirmationOrchestrator = orchestrator(
    presentDiscardClosureConfirmation,
    async actionMessage => {
        await presentClosureConfirmation(actionMessage.callBack);
    }
);

async function presentClosureConfirmation(callBack?: () => void) {
    // Call prepareEvent() since we want to extract the properties from the viewState before generating event diff
    prepareEvent();

    const { event, originalEvent } = getComposeStore();
    const { showConfirmationDialog, title, subText, okText, cancelText } = getDialogStrings(
        event,
        originalEvent
    );
    if (
        !showConfirmationDialog ||
        (await confirm(title, subText, false, {
            // Wait until dialog is dismissed to avoid focus interruption
            delayCallbackAfterAnimation: true,
            okText: okText,
            cancelText: cancelText,
        })) === DialogResponse.ok
    ) {
        if (callBack) {
            discardEvent(false);
            callBack();
        } else {
            discardEvent();
        }
    }
}

function getDialogStrings(
    event: CalendarEvent,
    originalEvent: CalendarEvent
): {
    showConfirmationDialog: boolean;
    title?: string;
    subText?: string;
    okText?: string;
    cancelText?: string;
} {
    const isExistingEvent = !isNewEvent();
    const {
        isNonAttachmentsDirty,
        isAttachmentsAddedDirty,
        isAttachmentsRemovedDirty,
    } = isFormDirty(event, originalEvent);

    if (isAnyNonAutoRunUilessAddinRunning(getComposeHostItemIndex(event.ParentFolderId.Id))) {
        return {
            showConfirmationDialog: true,
            title: loc(blockOnNavigationTitle),
            subText: loc(blockOnNavigationMessage),
            okText: loc(blockOnNavigationContinueLabel),
            cancelText: loc(blockOnNavigationCancelLabel),
        };
    }

    if (
        (!isExistingEvent && (isNonAttachmentsDirty || isAttachmentsAddedDirty)) ||
        originalEvent.IsDraft
    ) {
        return {
            showConfirmationDialog: true,
            title: loc(confirmDiscardEventTitleText),
            subText: loc(confirmDiscardEventSubText),
        };
    }

    if (isExistingEvent && (isNonAttachmentsDirty || isAttachmentsAddedDirty)) {
        return {
            showConfirmationDialog: true,
            title: loc(confirmDiscardChangesTitleText),
            subText: loc(confirmDiscardChangesSubText),
        };
    }

    // Only show attachments changed confirmation dialog when attachments
    // have been removed during compose —  added attachments will be
    // deleted if the user confirms discard
    if (
        isExistingEvent &&
        !isNonAttachmentsDirty &&
        isAttachmentsRemovedDirty &&
        isEventAMeeting(event) &&
        event.MeetingRequestWasSent
    ) {
        return {
            showConfirmationDialog: true,
            title: loc(confirmAttachmentsChangedTitleText),
            subText: loc(confirmAttachmentsChangedSubText),
        };
    }

    return {
        showConfirmationDialog: false,
    };
}

function isFormDirty(
    event: CalendarEvent,
    originalEvent: CalendarEvent
): {
    isNonAttachmentsDirty: boolean;
    isAttachmentsAddedDirty: boolean;
    isAttachmentsRemovedDirty: boolean;
} {
    const fullComposeViewState = getFullComposeViewState();
    const isSchedulingAssistantDirty = fullComposeViewState
        ? fullComposeViewState.showSchedulingAssistant && getSchedulingAssistantIsDirty()
        : false;

    const areNonAttachmentChangesDirty = isDirty(
        event,
        originalEvent,
        true /*shouldIgnoreAttachments*/
    );
    const isNonAttachmentsDirty = areNonAttachmentChangesDirty || isSchedulingAssistantDirty;
    const { isAttachmentsAddedDirty, isAttachmentsRemovedDirty } = getChangedAttachmentCount(
        event,
        originalEvent
    );
    return {
        isNonAttachmentsDirty: isNonAttachmentsDirty,
        isAttachmentsAddedDirty: isAttachmentsAddedDirty,
        isAttachmentsRemovedDirty: isAttachmentsRemovedDirty,
    };
}

function getChangedAttachmentCount(
    event: CalendarEvent,
    originalEvent: CalendarEvent
): { isAttachmentsAddedDirty: boolean; isAttachmentsRemovedDirty: boolean } {
    // Added attachments will be discarded so only interested in removed attachments
    const { addedAttachmentIds, removedAttachmentIds } = getChangedAttachmentIds(
        event,
        originalEvent
    );
    return {
        isAttachmentsAddedDirty: !!addedAttachmentIds.length,
        isAttachmentsRemovedDirty: !!removedAttachmentIds.length,
    };
}
